import { gql } from '@apollo/client';
import { Box, VStack } from '@chakra-ui/react';
import { DocumentNode } from 'graphql';
import * as React from 'react';

import { AbbreviateRedraft } from '@/components/AbbreviateRedraft';
import { AttachmentDisplay } from '@/components/AttachmentDisplay';
import { useModeration } from '@/components/Post/hooks/useModeration';
import {
  PlatformContextPreviewLink,
  useLinkPreview,
} from '@/imports/ui/components/context/PlatformContextPreviewLink';

import { AnswerContent_AnswerV2Fragment } from './graphql-types/AnswerContent_AnswerV2Fragment';

import { DeletedHeader } from '../DeletedHeader';
import { ImageDisplay } from '../ImageDisplay';
import { LikeCommentFooter } from '../LikeCommentFooter';
import { Moderation } from '../Moderation';
import { ModerationHeader } from '../ModerationHeader';
import { PinnedHeader } from '../PinnedHeader';
import { PostHeader } from '../PostHeader';
import { Tags } from '../Tags';
import { UpvoteDownvoteFooter } from './components/UpvoteDownvoteFooter';

type PostHeaderProps = React.ComponentProps<typeof PostHeader>;

export const AnswerContent: React.FC<{
  answer: AnswerContent_AnswerV2Fragment;
  options?: PostHeaderProps['options'];
  onClickComment: () => void;
  onClickCommentCount: () => void;
}> & {
  answerFragment: DocumentNode;
} = ({ answer, options, onClickComment, onClickCommentCount, children }) => {
  const { linkPreview } = useLinkPreview({
    rawDraftContentState: answer.answerBodyContentState,
  });
  const { shouldHidePost, shouldShowHeader } = useModeration({
    post: answer,
    isAnswerHeader: true,
  });

  return shouldHidePost ? (
    <Moderation post={answer} />
  ) : (
    <>
      {answer.deleted && <DeletedHeader post={answer} isAnswerHeader={true} />}
      {shouldShowHeader && (
        <ModerationHeader post={answer} isAnswerHeader={true} />
      )}
      <PinnedHeader post={answer} />
      <Box p="4">
        <PostHeader post={answer} mb="4" options={options} />
        {answer.answerBodyContentState && (
          <AbbreviateRedraft
            rawDraftContentState={answer.answerBodyContentState}
            noOfCharacters={500}
          />
        )}
        <VStack spacing="4" width="full" align="start" mt="4">
          <PlatformContextPreviewLink
            linkPreview={linkPreview}
            canEdit={false}
            onClose={() => null}
            w="full"
          />
          <ImageDisplay postEmbeddedImage={answer.embeddedImagesV2} />
          <AttachmentDisplay versionFiles={answer.versionFiles} />
          <Tags post={answer} my="4" />
          <UpvoteDownvoteFooter answer={answer} />
          <LikeCommentFooter
            post={answer}
            mt="4"
            onClickComment={onClickComment}
            onClickCommentCount={onClickCommentCount}
          />
          {children}
        </VStack>
      </Box>
    </>
  );
};

AnswerContent.answerFragment = gql`
  fragment AnswerContent_AnswerV2Fragment on AnswerV2 {
    _id
    deleted
    answerBodyContentState: bodyContentState {
      ...useLinkPreviewFragment
    }
    ...PostHeader_PostFragment
    ...Tags_PostFragment
    ...LikeCommentFooter_PostFragment
    versionFiles {
      _id
      ...AttachmentDisplay_VersionFileFragment
    }
    embeddedImagesV2 {
      ...ImageDisplay_PostEmbeddedImageFragment
    }
    ...useModeration_PostFragment
    ...ModerationHeader_PostFragment
    ...Moderation_PostFragment
    ...PinnedHeader_PostFragment
    ...UpvoteDownvoteFooter_AnswerFragment
  }
  ${useLinkPreview.rawDraftContentStateFragment}
  ${PostHeader.postFragment}
  ${Tags.postFragment}
  ${LikeCommentFooter.postFragment}
  ${AttachmentDisplay.versionFileFragment}
  ${ImageDisplay.postEmbeddedImageFragment}
  ${useModeration.postFragment}
  ${Moderation.postFragment}
  ${ModerationHeader.postFragment}
  ${PinnedHeader.postFragment}
  ${UpvoteDownvoteFooter.answerFragment}
`;
